import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { vacationRequestService } from '../../services/vacationRequestService';
import { vacationBalanceService } from '../../services/vacationBalanceService';
import { useCollaboratorsRealtimeQuery } from '@/features/Collaborators/Data/hooks/useCollaboratorsRealtimeQuery';
import type { VacationRequest, VacationBalance } from '../../models/vacation.types';
import type { Collaborator } from '@/features/Collaborators/Models/collaborator.types';

export const VACATION_HISTORY_DATA_QUERY_KEY = 'vacationHistoryData';

export interface CollaboratorMaps {
    byId: Map<string, { name: string; email: string }>;
    byEmail: Map<string, { name: string; id: string }>;
}

export interface VacationHistoryData {
    requests: VacationRequest[];
    collaborators: Collaborator[];
    collaboratorMaps: CollaboratorMaps;
    balancesById: Map<string, VacationBalance>;
    isLoading: boolean;
}

export function useVacationHistoryDataQuery(): VacationHistoryData {
    const { data: requests = [], isLoading: requestsLoading } = useQuery({
        queryKey: [VACATION_HISTORY_DATA_QUERY_KEY, 'requests'],
        queryFn: async () => vacationRequestService.listAll(),
        staleTime: 5 * 60 * 1000,
    });

    const { data: collaborators = [], isLoading: collaboratorsLoading } = useCollaboratorsRealtimeQuery({
        withSalary: false,
        withResources: false,
    });

    const { data: balances = [], isLoading: balancesLoading } = useQuery({
        queryKey: [VACATION_HISTORY_DATA_QUERY_KEY, 'balances'],
        queryFn: async () => vacationBalanceService.listAll(),
        staleTime: 5 * 60 * 1000,
    });

    const collaboratorMaps = useMemo<CollaboratorMaps>(() => {
        const byId = new Map<string, { name: string; email: string }>();
        const byEmail = new Map<string, { name: string; id: string }>();

        collaborators.forEach((c) => {
            if (c.email) {
                byId.set(c.id, { name: c.name, email: c.email });
                byEmail.set(c.email.toLowerCase(), { name: c.name, id: c.id });
            }
        });

        return { byId, byEmail };
    }, [collaborators]);

    const balancesById = useMemo(() => {
        const map = new Map<string, VacationBalance>();
        balances.forEach((b) => map.set(b.collaboratorId, b));
        return map;
    }, [balances]);

    return {
        requests,
        collaborators,
        collaboratorMaps,
        balancesById,
        isLoading: requestsLoading || collaboratorsLoading || balancesLoading,
    };
}
